
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import RunnitImage from './RunnitImage.vue';
import RunnitVideo from './RunnitVideo.vue';
import RunnitLayers from './RunnitLayers.vue';
import RunnitAsset3D from './RunnitAsset3D.vue';
import { RunnitRemixMixin, RunnitsImageSelectMixin, ResultFavoritesMixin } from '@/mixins';
import _isEmpty from 'lodash/isEmpty';
import { ROUTER } from '@/router/constants';
import {
	extractPromptStringValue,
	RUNNIT_NODE_RUN_RESULT_TYPE,
	RunnitNodeField,
	RunnitNodeFieldGroup,
	RUNNIT_TYPE,
	RunnitNodeDef,
} from '@run-diffusion/shared';
import {
	GALLERY_SELECTION_CONTEXT_KEY,
	isGallerySelectionMode,
	isSelectedInGalleryContext,
} from '@/components/ImageGallery/gallerySelectionContext';
import { RUNNIT_BULK_ACTIONS_TRIGGER_ID } from './constants';

export default Vue.extend({
	name: 'RunnitResult',
	inject: {
		gallerySelectionContext: { from: GALLERY_SELECTION_CONTEXT_KEY, default: null },
	},
	props: {
		// Runnit Image Props
		timerStartAtMillis: { type: Number, default: null },
		nodeRunResult: { type: Object, default: null },
		upload: { type: Object, default: null },
		avatar: { type: Object, default: null },
		noClick: { type: Boolean, default: false },
		isThumb: { type: Boolean, default: false },
		isAvatar: { type: Boolean, default: false },
		clearable: { type: Boolean, default: false },
		width: { type: String, default: null },
		height: { type: String, default: null },
		maxHeight: { type: String, default: null },
		isErrorPlaceholder: { type: Boolean, default: false },
		isLoadingPlaceholder: { type: Boolean, default: false },
		isInsideInfoCarousel: { type: Boolean, default: false },
		loadEager: { type: Boolean, default: false },
		isSelectedImage: { type: Boolean, default: false },
		allowFullScreen: { type: Boolean, default: false },
		useZoomViewer: { type: Boolean, default: false },
		coldStartWarningSeconds: { type: Number, default: null },
		isNsfwPlaceholder: { type: Boolean, default: false },
		nodeDef: { type: Object, default: null },
		nodeRunId: { type: String, default: null },
		nodeRun: { type: Object, default: null },
		hidePromptOverlay: { type: Boolean, default: false },
		hideToolName: { type: Boolean, default: false },
		draggable: { type: Boolean, default: false },
		enableComparison: { type: Boolean, default: true },
		hasPrev: { type: Boolean, default: false },
		hasNext: { type: Boolean, default: false },
		asset3DInteractive: { type: Boolean, default: false },
	},
	mixins: [
		RunnitRemixMixin,
		RunnitsImageSelectMixin,
		ResultFavoritesMixin,
	],
	data () {
		return {
			RUNNIT_NODE_RUN_RESULT_TYPE,
			imageDimensions: {
				width: 0,
				height: 0
			},
			isHovering: false,
			isDraggingAttempt: false,
			mouseDownX: 0,
			mouseDownY: 0,
		};
	},
	computed: {
		...mapState([
			'user',
			'runnitState',
			'dragAndDrop',
		]),
		isSmallImage () {
			return this.imageDimensions.width < 120 || this.imageDimensions.height < 120;
		},
		nsfwModerationTitle () {
			const internalNsfw = this.nodeRun?.internalNsfw ?? this.nodeRunResult?.internalNsfw;
			return internalNsfw ? 'Moderated by RunDiffusion' : 'Moderated by Model';
		},
		promptField () {
			if (!this.nodeRun || !this.nodeRun.fields) {
				return null;
			}

			// Find the prompt field in the nodeRun fields
			return this.findPromptField(this.nodeRun.fields);
		},
		showPromptMagic () {
			return !!(
				this.promptField &&
				this.nodeRun.promptMagic &&
				this.nodeRun.promptMagic.results &&
				this.nodeRun.promptMagic.results[this.promptField.fieldDefUuid] &&
				this.nodeRun.inputs &&
				extractPromptStringValue(this.nodeRun.inputs[this.promptField.fieldDefUuid]) !== this.nodeRun.promptMagic.results[this.promptField.fieldDefUuid]
			);
		},
		promptText () {
			if (!this.promptField || !this.nodeRun.inputs) {
				return null;
			}

			// Get Prompt Magic prompt if exists
			if (this.showPromptMagic) return this.nodeRun.promptMagic.results[this.promptField.fieldDefUuid];

			// Get the prompt value from the inputs using the fieldDefUuid
			const promptValue: string = this.nodeRun.inputs[this.promptField.fieldDefUuid];
			return extractPromptStringValue(promptValue);
		},
		isRemixable () {
			return this.canRemix(this.nodeRun);
		},
		selectionType () {
			return this.nodeRunResult?.type || RUNNIT_NODE_RUN_RESULT_TYPE.IMG;
		},
		isSelectionMode () {
			if (this.gallerySelectionContext) {
				return isGallerySelectionMode(this.gallerySelectionContext, this.selectionType, this.noClick);
			}
			return !!(!this.noClick && this.currentSelectionState.nodeRunResultSelectionMode && this.currentSelectionState.canSelectTypes.includes(this.selectionType));
		},
		isSelected () {
			if (this.gallerySelectionContext) {
				return isSelectedInGalleryContext(this.gallerySelectionContext, {
					nodeRunResultUuid: this.selectableNodeRunResult?.uuid || null,
				});
			}
			return !!(
				this.currentSelectionState.selectedNodeRunResults.length &&
				this.selectableNodeRunResult &&
				this.currentSelectionState.selectedNodeRunResults.some((result) => result.uuid === this.selectableNodeRunResult.uuid)
			);
		},
		isSelectableNsfwPlaceholder () {
			const triggeringId: string | null = this.gallerySelectionContext
				? this.gallerySelectionContext.triggeringId
				: this.currentSelectionState?.triggeringId;
			return !!this.nodeRunResult && this.isSelectionMode && triggeringId === RUNNIT_BULK_ACTIONS_TRIGGER_ID;
		},
		selectableNodeRunResult () {
			if (!this.nodeRunResult) return null;
			return {
				...this.nodeRunResult,
				type: this.selectionType,
				...(this.isNsfwPlaceholder && { isNsfw: true }),
			};
		},
		batchIdForBadge (): string | null {
			const id = this.nodeRun && this.nodeRun.batchInfo && this.nodeRun.batchInfo.batchId;
			if (typeof id !== 'string' || !id.trim()) {
				return null;
			}
			return id;
		},
		showBatchBadge (): boolean {
			return !!this.batchIdForBadge && !this.isSelectionMode && !this.noClick;
		},
		isBatchBadgeAboveLayersPill (): boolean {
			return !!(
				this.nodeRunResult &&
				(
					this.nodeRunResult.type === RUNNIT_NODE_RUN_RESULT_TYPE.LAYERS ||
					this.nodeRunResult.type === RUNNIT_NODE_RUN_RESULT_TYPE.ASSET_3D
				)
			);
		},
		isComparisonActive () {
			return !!(this.runnitState && this.runnitState.imageComparisonMode);
		},
		canShowFavoriteHeart (): boolean {
			return this.canFavoriteResult(this.nodeRun, this.nodeRunResult);
		},
		isThisResultFavorited (): boolean {
			return this.isResultFavorited(this.nodeRunResult);
		},
		favoriteHeartContextAllowed (): boolean {
			// Same view-context guards the hover overlay uses, so the persistent
			// favorited heart never appears in selection / fullscreen / zoom /
			// comparison / drag contexts where the heart was previously hidden.
			// (The md+ breakpoint gate stays in the template, matching convention.)
			return this.canShowFavoriteHeart
				&& !this.isSelectionMode
				&& !this.allowFullScreen
				&& !this.useZoomViewer
				&& !this.noClick
				&& !this.isComparisonActive
				&& !(this.draggable && this.dragAndDrop && this.dragAndDrop.isDragging)
				&& !this.isDraggingAttempt;
		},
		effectiveComparisonSourceImageUrl () {
			// Get the source image URL for the currently active comparison field
			if (this.runnitState && this.runnitState.comparisonFieldDefUuid && this.runnitState.comparisonFieldSourceImageUrls) {
				return this.runnitState.comparisonFieldSourceImageUrls[this.runnitState.comparisonFieldDefUuid] || null;
			}
			return null;
		},
	},
	mounted () {
		// If RunnitImage exposes dimensions directly, try to get them
		if (this.$refs.runnitImage && (this.$refs.runnitImage as any).dimensions) {
			this.imageDimensions = (this.$refs.runnitImage as any).dimensions;
		}
	},
	methods: {
		...mapActions([
		]),
		_isEmpty,
		handleMouseDown (event: MouseEvent) {
			if (this.draggable) {
				this.mouseDownX = event.clientX;
				this.mouseDownY = event.clientY;
				this.isDraggingAttempt = false;
			}
		},
		handleMouseMove (event: MouseEvent) {
			if (this.draggable && (this.mouseDownX !== 0 || this.mouseDownY !== 0)) {
				const deltaX = Math.abs(event.clientX - this.mouseDownX);
				const deltaY = Math.abs(event.clientY - this.mouseDownY);
				// If mouse moved more than 5px, consider it a drag attempt
				if (deltaX > 5 || deltaY > 5) {
					this.isDraggingAttempt = true;
				}
			}
		},
		handleMouseUp () {
			this.mouseDownX = 0;
			this.mouseDownY = 0;
			// Reset after a short delay to allow drag to complete
			setTimeout(() => {
				this.isDraggingAttempt = false;
			}, 100);
		},
		handleOverlayMouseDown (event: MouseEvent) {
			// Check if the click is on an interactive element (button, link, etc.)
			const target = event.target as HTMLElement;
			const isInteractiveElement = target.closest('button, a, [role="button"], .clickable');

			// If clicking on an interactive element, don't interfere with the click
			if (isInteractiveElement) {
				return;
			}

			// If image is draggable, allow the event to pass through to start drag
			if (this.draggable) {
				// Don't prevent default, let it bubble to the image
				event.stopPropagation();
				// Hide overlay immediately to allow drag
				this.isDraggingAttempt = true;
			}
		},
		onDimensionsUpdated (dimensions) {
			this.imageDimensions = dimensions;
		},
		findPromptField (fields: (RunnitNodeField | RunnitNodeFieldGroup)[]): RunnitNodeField {
			// Recursively search for a field with type 'PROMPT'
			for (const field of fields) {
				const f: RunnitNodeField = field as RunnitNodeField;
				const g: RunnitNodeFieldGroup = field as RunnitNodeFieldGroup;
				if (f.__rfield && f.type === 'PROMPT') {
					return f;
				} else if (g.__rgroup && g.fields) {
					const foundField: RunnitNodeField = this.findPromptField(g.fields);
					if (foundField) {
						return foundField;
					}
				}
			}
			return null;
		},
		onSingleSelect (event) {
			this.$emit('on-single-select', event);
		},
		onMultiSelect (event) {
			this.$emit('on-multi-select', event);
		},
		onClick (event) {
			this.$emit('on-click');
		},
		onNsfwClick (event) {
			if (this.isSelectableNsfwPlaceholder) {
				this.emitSelection();
				return;
			}
			this.onClick(event);
		},
		emitSelection () {
			this.$emit('on-multi-select', {
				nodeRunResult: this.selectableNodeRunResult,
				upload: this.upload,
				avatar: this.avatar,
			});
		},
		onClear (event) {
			this.$emit('on-clear');
		},
		handleRemixClick (nodeDef, nodeRunId, nodeRun) {
			if (!this.user) {
				const currentPath: string = this.$router.resolve({
					name: ROUTER.RUNNIT_REMIX,
					query: {
						rnrid: nodeRunId,
						ruuid: this.nodeRunResult.uuid,
						autoRemix: true,
					},
				}).href;
				this.routerPush(this.$route, this.$router, {
					name: ROUTER.LOGIN,
					query: { redirect: currentPath }
				});
			} else {
				this.openToolWithSameInputs(nodeDef, nodeRun, nodeRunId, nodeRun ? nodeRun.runnitId : null, nodeRun ? nodeRun.nodeId : null);
			}
		},
		handleTitleClick (nodeDef: RunnitNodeDef) {
			if (this.user) {
				this.onAddRunnitClick(nodeDef, RUNNIT_TYPE.SINGLE_TOOL);
			}
		},
		async handleFavoriteHeartClick () {
			if (!this.canShowFavoriteHeart) return;
			await this.toggleResultFavorite(this.nodeRun, this.nodeRunResult);
		},
	},
	components: {
		RunnitImage,
		RunnitVideo,
		RunnitLayers,
		RunnitAsset3D,
	},
});
